import { getPanelTestKindsLabel, getProviderLabel } from "../store/helper";

/**
 * Normalizes a lab-panel *results* response into the slim shape the results UI
 * needs, and shields the UI from the current fat backend payload.
 *
 * TODO(backend): once the backend delivers the trimmed structure (see the
 * proposal handed over — panel-level `testType`/`sampleDate`/`orderNumber`/
 * `panelOrigin` and item-level `scoreCategory`), this normalizer becomes a
 * near-passthrough: it already prefers those fields when present and only falls
 * back to deriving them from today's payload.
 */

export interface BiomarkerResult {
  id: string;
  name: string;
  category: number;
  // 1 quantitative | 2 range/threshold | 3 qualitative | 0 no result
  type: number;
  value: number | null;
  qvalue: string | null;
  unit: string;
  // Reference-range string, shown in the right column.
  label: string;
  // type-2 range value bounds. "<"/"<=" live on the max bound, ">"/">=" on the
  // min bound; a two-sided range fills both values. See formatBiomarkerResultValue.
  rangeMinOp: string;
  rangeMinValue: number | null;
  rangeMaxOp: string;
  rangeMaxValue: number | null;
  // Color category (1-4, same scale the biomarker tab colors by). 0 = no result.
  scoreCategory: number;
  // true when the lab returned this biomarker even though it wasn't in the
  // original order (backend `added === 1`). Flagged in the results UI.
  added: boolean;
  // When this result was last updated by the lab; shown as the row's date column.
  dateUpdated: string | null;
  // true when the lab couldn't process this biomarker (backend `failed !== 0`).
  // Such items are surfaced in a notice (with `reason`) instead of the table.
  failed: boolean;
  reason: string | null;
}

export interface LabPanelResults {
  id: string;
  customerID: string;
  name: string;
  panelStatus: number;
  provider: number | string;
  testType: string;
  total: number;
  panelOrigin: string;
  dateCreated: string;
  sampleDate: string | null;
  orderNumber: string | null;
  customerFirstName: string;
  customerLastName: string;
  items: BiomarkerResult[];
}

/**
 * INTERIM: derive the color category from the legacy `interpretation` field
 * until the backend sends a real `scoreCategory`. We only speak of blood
 * biomarkers, so we stay within the biomarker-tab's category coloring:
 *   in range (interpretation 1)  → 2 (blue)
 *   out of range (interpretation -1) → 1 (amber)
 *   no result (type 0 / other)   → 0 (neutral default)
 */
const interpretationToScoreCategory = (item: any): number => {
  if (!item || item.type === 0) return 0;
  if (item.interpretation === -1) return 1;
  if (item.interpretation === 1) return 2;
  return 0;
};

const MAX_BOUND_OPS = ["<", "<="];
const MIN_BOUND_OPS = [">", ">="];

/**
 * Canonical type-2 bounds. Prefers the new backend fields; otherwise derives
 * them from the legacy `range_min_*`/`range_max_*` payload, reassigning each
 * operator to the correct bound (`<`/`<=` → max, `>`/`>=` → min) since today's
 * payload parks single "<n" thresholds on the min bound.
 */
const deriveBounds = (
  item: any
): Pick<
  BiomarkerResult,
  "rangeMinOp" | "rangeMinValue" | "rangeMaxOp" | "rangeMaxValue"
> => {
  if (
    item?.rangeMinOp !== undefined ||
    item?.rangeMaxOp !== undefined ||
    item?.rangeMinValue !== undefined ||
    item?.rangeMaxValue !== undefined
  ) {
    return {
      rangeMinOp: item.rangeMinOp ?? "",
      rangeMinValue: item.rangeMinValue ?? null,
      rangeMaxOp: item.rangeMaxOp ?? "",
      rangeMaxValue: item.rangeMaxValue ?? null,
    };
  }

  let rangeMinOp = "";
  let rangeMinValue: number | null = null;
  let rangeMaxOp = "";
  let rangeMaxValue: number | null = null;

  const consider = (op: any, val: any) => {
    const o = typeof op === "string" ? op : "";
    const v = typeof val === "number" ? val : null;
    if (v === null) return;
    if (MAX_BOUND_OPS.includes(o)) {
      rangeMaxOp = o;
      rangeMaxValue = v;
    } else if (MIN_BOUND_OPS.includes(o)) {
      rangeMinOp = o;
      rangeMinValue = v;
    } else if (v !== 0) {
      // No operator + non-zero → part of a two-sided inclusive range.
      if (rangeMinValue === null) rangeMinValue = v;
      else rangeMaxValue = v;
    }
  };
  consider(item?.range_min_op, item?.range_min_value);
  consider(item?.range_max_op, item?.range_max_value);

  return { rangeMinOp, rangeMinValue, rangeMaxOp, rangeMaxValue };
};

const normalizeItem = (item: any): BiomarkerResult => ({
  id: item?.id,
  name: item?.name ?? "",
  category: item?.category ?? 0,
  type: item?.type ?? 0,
  value: item?.value ?? null,
  qvalue: item?.qvalue ?? null,
  unit: item?.unit ?? "",
  label: item?.label ?? "",
  ...deriveBounds(item),
  scoreCategory:
    item?.scoreCategory != null
      ? item.scoreCategory
      : interpretationToScoreCategory(item),
  added: item?.added === 1 || item?.added === true,
  dateUpdated: item?.dateUpdated ?? null,
  failed: item?.failed != null && Number(item.failed) !== 0,
  reason:
    item?.reason ??
    item?.failedReason ??
    item?.failReason ??
    item?.message ??
    item?.error ??
    null,
});

// `panelType` 1 = a panel the company built ("Custom"); anything else is the
// lab's own catalogue panel, named after its provider ("Quest Diagnostics").
export const getPanelOrigin = (panel: any): string =>
  panel?.panelType === 1
    ? "Custom"
    : getProviderLabel(panel?.provider) || "Custom";

export const normalizeLabPanelResults = (panel: any): LabPanelResults => {
  const items: BiomarkerResult[] = Array.isArray(panel?.items)
    ? panel.items.map(normalizeItem)
    : [];

  return {
    id: panel?.id,
    customerID: panel?.customerID,
    name: panel?.name ?? "",
    panelStatus: panel?.panelStatus,
    provider: panel?.provider,
    // Backend `testType` when present, else derived from the item test kinds.
    testType: panel?.testType ?? getPanelTestKindsLabel(panel),
    total: panel?.total ?? 0,
    panelOrigin: getPanelOrigin(panel),
    dateCreated: panel?.dateCreated,
    sampleDate: panel?.sampleDate ?? null,
    orderNumber: panel?.orderNumber ?? null,
    customerFirstName: panel?.customerFirstName ?? "",
    customerLastName: panel?.customerLastName ?? "",
    items,
  };
};
